
import React from "react";
import { Pencil, Trash2, MoreHorizontal, Eye, EyeOff } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CollectionContextMenuProps {
  children: React.ReactNode;
  collectionId: string;
  collectionName: string;
  collectionColor?: string;
  isHidden?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleVisibility?: (id: string) => void;
}

const CollectionContextMenu = ({
  children,
  collectionId,
  collectionName,
  collectionColor,
  isHidden = false,
  onEdit,
  onDelete,
  onToggleVisibility,
}: CollectionContextMenuProps) => {
  // Ensure we have a valid display name (shouldn't be needed due to upstream fix, but just in case)
  const displayName = collectionName && collectionName.trim() !== '' 
    ? collectionName 
    : `Collection ${collectionId.substring(0, 4)}`;
  
  return (
    <div 
      className={cn("flex items-center w-full justify-between group relative hover:bg-clustr-lightGray h-8", {
        "opacity-50": isHidden
      })} 
      onClick={(e) => e.stopPropagation()}
    >
      {children}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 rounded-full absolute right-2 z-10 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            <MoreHorizontal className="h-4 w-4 text-clustr-darkGray" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="right" className="w-56">
          <DropdownMenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onEdit) onEdit(collectionId);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onToggleVisibility) onToggleVisibility(collectionId);
            }}
          >
            {isHidden ? (
              <>
                <Eye className="mr-2 h-4 w-4" />
                Show Collection
              </>
            ) : (
              <>
                <EyeOff className="mr-2 h-4 w-4" />
                Hide Collection
              </>
            )}
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onDelete) onDelete(collectionId);
            }} 
            className="text-red-500 hover:text-red-500 focus:text-red-500"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default CollectionContextMenu;
